import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { MatDialog } from '@angular/material/dialog';
import { FleetUser, ErmDevOpsApiClientService, CreateFleetUserDialogData, DeleteFleetUserRequest, UpdateFleetUserDialogData } from 'erm-sdk';
import { CreateFleetUserComponent } from '../create-fleet-user/create-fleet-user.component';
import * as _ from 'lodash';
import { UpdateFleetUserComponent } from '../update-fleet-user/update-fleet-user.component';

@Component({
  standalone: false,
  selector: 'app-fleet-users',
  templateUrl: './fleet-users.component.html',
  styleUrls: ['./fleet-users.component.scss']
})
export class FleetUsersComponent implements OnInit {

  isLoading = false;
  customerId: string;
  fleetKindList?: string[];
  selectedFleetUser?: FleetUser;
  fleetUsers?: FleetUser[];
  fleetUsersGridOptions: GridOptions;
  fleetUsersGridApi?: GridApi;
  constructor(private ermDevOpsApiClient: ErmDevOpsApiClientService, private matdialog: MatDialog, route: ActivatedRoute) {
    this.customerId = route.snapshot.params['customerId'];

    // Fleet Users Ag-Grid Options
    this.fleetUsersGridOptions = {
      columnDefs: this.createFleetUsersColumnDefs(),
      enableCellTextSelection: true,
      suppressCellFocus: true,
      rowSelection: { mode: 'singleRow' },
      onSelectionChanged: event => {
        this.selectedFleetUser = _.first(event.api.getSelectedNodes().map(x => x.data));
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true
      },
      onGridReady: (params: GridReadyEvent) => {
        this.fleetUsersGridApi = params.api;
      }
    };
  }

  async ngOnInit(): Promise<void> {
    try {

      this.isLoading = true;
      // Get Fleet Users
      const getFleetUsersResponse = await this.ermDevOpsApiClient.getFleetUsers(this.customerId);
      this.fleetUsers = getFleetUsersResponse.body?.fleetUsers || new Array<FleetUser>();

      // Initialize ag-grid
      this.fleetUsersGridApi?.setGridOption('rowData', this.fleetUsers);
    } finally {
      this.isLoading = false;
    }
  }

  private createFleetUsersColumnDefs(): any[] {
    return [
      { headerName: 'User Email', field: 'userId',
        cellRenderer: (params: any): string => {
          return `${params.value.split(":")[1]}`;
        },
     },
      { headerName: 'Fleet Kind', field: 'fleetKind' },
      { headerName: 'Enabled', field: 'isEnabled' }
    ];
  }

  async onCreateFleetUser(): Promise<void> {
    try {
      this.isLoading = true;

      if (!this.fleetKindList) {
        const getSupportedFleetKind = await this.ermDevOpsApiClient.getFleetConstantClassCollection();
        this.fleetKindList = getSupportedFleetKind.body?.fleetConstantClassCollection?.fleetKindList || [];
      }

      const createFleetUserDialogRef = this.matdialog.open(CreateFleetUserComponent, {
        data: {
          customerId: this.customerId,
          fleetKindList: this.fleetKindList
        } as CreateFleetUserDialogData
      });
      
      createFleetUserDialogRef.afterClosed().subscribe(async dialogResult => {
        const result = dialogResult;
        if (result && result.data) {
          // Add to fleet user grid
          this.fleetUsersGridOptions['api']?.applyTransaction({
            add: [result.data as FleetUser]
          });
        }
      });
    } finally {
      this.isLoading = false;
    }
  }

  canCreateFleetUser(): boolean {
    return !this.customerId;
  }

  async onUpdateFleetUser(): Promise<void> {
    try {
      this.isLoading = true;

      if (!this.fleetKindList) {
        const getSupportedFleetKind = await this.ermDevOpsApiClient.getFleetConstantClassCollection();
        this.fleetKindList = getSupportedFleetKind.body?.fleetConstantClassCollection?.fleetKindList || [];
      }

      const updateFleetUserDialogRef = this.matdialog.open(UpdateFleetUserComponent, {
        data: {
          fleetUser: this.selectedFleetUser,
          fleetKindList: this.fleetKindList
        } as UpdateFleetUserDialogData
      });

      updateFleetUserDialogRef.afterClosed().subscribe(async dialogResult => {
        const result = dialogResult;
        if (result && result.data) {
          // update selected user grid data
          const dataToUpdate = this.fleetUsersGridOptions['api']?.getSelectedNodes()[0].data;
          dataToUpdate.isEnabled = result.data.isEnabled;
          this.fleetUsersGridOptions['api']?.applyTransaction({ update: [dataToUpdate] });
        }
      });
    } finally {
      this.isLoading = false;
    }
  }

  canUpdateFleetUser(): boolean {
    return !this.selectedFleetUser;
  }

  async deleteFleetUser(): Promise<void> {
    try {
      this.isLoading = true;
      let fleetUser = this.selectedFleetUser as FleetUser;
      let customerIdAndUserEmail = (fleetUser.userId as string).split(":");
      const request = new DeleteFleetUserRequest();
      request.customerId = customerIdAndUserEmail[0];
      request.userEmail = customerIdAndUserEmail[1];
      request.fleetKind = fleetUser.fleetKind;
      await this.ermDevOpsApiClient.deleteFleetUser(request);
      // remove selected item from fleet user grid
      this.fleetUsersGridOptions['api']?.applyTransaction({
        remove: [this.selectedFleetUser]
      });
      this.selectedFleetUser = undefined;
    } finally {
      this.isLoading = false;
    }
  }

  canDeleteFleetUser(): boolean {
    return !this.selectedFleetUser;
  }
}
